import { useEffect } from 'react';

import { useAuth } from './EsAuth';
import { useEsPageMetaValue } from '../../common/EsPageMeta';
import { useEsStrings } from '../../common/EsStrings';

// The shell's own description, put back when a page that set its own leaves.
let shellDescription = null;

const descriptionTag = () => document.querySelector( 'meta[name="description"]' );

// The shell's own og:title and the rest, put back as the description is. Read once from
// the page: nothing in esfront writes them but this.
const SHARE_TAGS = [ 'og:title', 'twitter:title', 'og:description', 'twitter:description' ];
let shellShare = null;

const shareTag = ( pName ) => document.querySelector( `meta[property="${ pName }"]` );

// A page that names itself names it for a share preview too: og:title and twitter:title
// read its title, og:description and twitter:description its description. A crawler that
// runs no script sees index.html's own, which say the site.
function setShare( pTitle, pDescription ) {
    SHARE_TAGS.forEach( ( pName ) => {
        const aTag = shareTag( pName );
        if ( !aTag ) return;
        const aOwn = shellShare?.[ pName ];
        const aValue = pName.endsWith( 'title' ) ? pTitle : pDescription;
        aTag.setAttribute( 'content', aValue ?? aOwn ?? '' );
    } );
}

/**
 * What the browser tab says.
 *
 * <p>Logged out it is "Escapade", which is what {@code index.html} ships and
 * what a visitor should see. Logged in it becomes
 * <b>Escapade &lt;version&gt; - &lt;nickname&gt;</b>, at Tim's word on
 * 2026-09-17.
 *
 * <p><b>Why a title is worth this much code.</b> The first question about any
 * reported behaviour is which build it was and who was looking at it, and a tab
 * strip answers both in a screenshot -- which is how most of what gets reported
 * arrives. The footer already prints the version for the same reason; this puts
 * it somewhere that survives being cropped.
 *
 * <p>Effect-only and renders null, the same shape as EsScrollToTop beside it in
 * EsParent. It is a component rather than a hook called from EsParent so that
 * EsParent stays pure layout, and so that this sits in one place rather than
 * being something every route has to remember.
 *
 * <h2>The three ways it degrades, each on purpose</h2>
 *
 * <ul>
 *   <li><b>No nickname.</b> An account can arrive by a route that supplies no
 *       name -- a federated login with no profile claim -- and esback's
 *       displayName falls back to the address's local part, but the /authUser
 *       body is not obliged to carry one. The title drops the dash and the name
 *       rather than rendering "Escapade 0.6.6 - null" or a bare trailing dash.</li>
 *   <li><b>No version.</b> ES_VERSION is substituted by webpack from
 *       package.json, so it is present in every real build -- but a bundle built
 *       some other way has it undefined, and "Escapade undefined - simon" is
 *       worse than no version at all. Same guard the footer uses.</li>
 *   <li><b>Still loading.</b> auth.loading is true until /authUser answers, and
 *       `authenticated` is false during it. The title is therefore "Escapade"
 *       for that moment and then becomes the full one, which is the right way
 *       round: a tab that briefly says the product name and then says who you
 *       are reads as a page loading, where the reverse would read as a logout.</li>
 * </ul>
 *
 * <p><b>A public codex page names itself.</b> The entry, section, or pack on screen sets
 * its own title and meta description (useEsPageMeta), and the tab then reads "Wolf - Escapade"
 * for everybody, a signed-in account included: the page is the same one a search engine was
 * shown. Every other page keeps the rules above.
 *
 * <p><b>THE ADDRESS MUST NEVER APPEAR HERE.</b> The nickname is the display
 * name; the identity is an email address. A tab title is
 * about the most public thing on a screen -- it survives screenshots, screen
 * shares and alt-tab previews -- so this reads account.nickname and nothing
 * else. See the note on EsAccount.nickname, which makes the same point about
 * rosters and chat logs.
 */
export default function EsPageTitle() {
    const auth = useAuth();

    const aNickname = auth.account?.nickname;
    const aVersion = process.env.ES_VERSION;
    const aMeta = useEsPageMetaValue();
    const t = useEsStrings();

    useEffect( () => {
        const aTag = descriptionTag();
        if ( aTag && shellDescription === null ) shellDescription = aTag.getAttribute( 'content' ) || '';
        if ( shellShare === null ) {
            shellShare = Object.fromEntries( SHARE_TAGS.map( ( pName ) => [ pName, shareTag( pName )?.getAttribute( 'content' ) ?? null ] ) );
        }

        if ( aMeta ) {
            document.title = aMeta.title + ' - Escapade';
            if ( aTag && aMeta.description ) aTag.setAttribute( 'content', aMeta.description );
            setShare( aMeta.title + ' - Escapade', aMeta.description || null );
            return;
        }
        setShare( null, null );
        // index.html's own description reads in the reader's locale; one nginx's SEO block wrote
        // for a page is that page's, and is put back as it came.
        const aShell = shellDescription === 'Tools for tabletop RPGs: a live game console, character sheets, a Creative Commons codex, and maps on a TV under your minis. $35 a year for the GM. Players are always free.'
            ? t( 'nav-page-description', 'Tools for tabletop RPGs: a live game console, character sheets, a Creative Commons codex, and maps on a TV under your minis. $35 a year for the GM. Players are always free.' ) : shellDescription;
        if ( aTag && shellDescription !== null ) aTag.setAttribute( 'content', aShell );

        const aParts = [ 'Escapade' ];

        if ( auth.authenticated ) {
            if ( aVersion ) aParts.push( aVersion );
            if ( aNickname ) aParts.push( '- ' + aNickname );
        }

        document.title = aParts.join( ' ' );
    }, [ auth.authenticated, aNickname, aVersion, aMeta, t ] );

    return null;
}
